import { useRef, useState } from "react";
import { Upload, Link2, X } from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";
import { API } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Resolve a possibly-relative media URL (e.g. "/api/uploads/abc.png") to a full URL
 * pointing at the backend so it loads correctly on the public site and admin.
 */
export function resolveMediaUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${BACKEND}${url}`;
  return url;
}

export default function MediaUpload({ label, value, onChange, accept = "image/*,video/*", testid }) {
  const { token } = useAdminAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/admin/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Error");
      const data = await res.json();
      onChange(data.url);
    } catch (e2) {
      setErr(e2.message || "Error al subir");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const preview = resolveMediaUrl(value);
  const isVideo = /\.(mp4|webm|mov)(\?.*)?$/i.test(value || "");

  return (
    <div data-testid={testid ? `media-${testid}` : "media-upload"}>
      <label className="text-xs uppercase tracking-wider text-white/50 block mb-1">{label}</label>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... o /api/uploads/..."
          className="flex-1 bg-white/5 border border-white/15 px-3 py-2 text-white text-sm outline-none focus:border-[#ff9c00]"
        />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="px-3 py-2 bg-[#ff9c00] text-black text-sm font-bold hover:bg-[#ffb133] inline-flex items-center gap-1.5 disabled:opacity-50" data-testid={testid ? `upload-${testid}` : "upload-btn"}>
          <Upload size={14}/> {busy ? "Subiendo..." : "Subir archivo"}
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")} className="px-3 py-2 border border-white/15 text-white/60 hover:text-red-300 hover:border-red-400">
            <X size={14}/>
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept={accept} onChange={handleFile} className="hidden"/>
      {err && <p className="text-red-400 text-xs mb-2">{err}</p>}
      {preview && (
        <div className="border border-white/10 bg-black/30 p-2 inline-block">
          {isVideo ? (
            <video src={preview} controls muted className="max-h-32 max-w-xs"/>
          ) : (
            <img src={preview} alt="preview" className="max-h-32 max-w-xs object-contain"/>
          )}
          <p className="text-xs text-white/40 mt-1 font-mono truncate max-w-xs flex items-center gap-1">
            <Link2 size={10}/> {value}
          </p>
        </div>
      )}
    </div>
  );
}
